import type { IIndexedValidationError } from "@common-shared/types/general.types"
import type { IPayerServicePayerMappingsBulkRowError } from "@common-shared/types/internalApis/payerService.types"
import type { IOVUpsertPayerMappingRequestData } from "@common-shared/types/requestData/oneVerifyRequestData.types"
import type { IOneVerifyPayerMapping } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { PAYER_MAPPINGS_CONFIG } from "../config/payerMappings.config"
import { RawPayerMappingCsvRowDTO } from "../dto/rawPayerMappingCsvRow.dto"
import { PayerMappingsExportRowKey } from "../types/payerMappings.types"
import { CsvHelper } from "./csv.helper"

export class PayerMappingsCsvHelper {
    /**
     * Parse CSV text (first row headers, then data).
     * Excludes empty lines and any line that starts with # (comment lines).
     */
    static parse(csvText: string): RawPayerMappingCsvRowDTO[] {
        return CsvHelper.parseHeaderMappedRows(csvText, {
            headerRowIndex: PAYER_MAPPINGS_CONFIG.csvHeaderRowIndex,
            headerToKey: PAYER_MAPPINGS_CONFIG.csvHeaderToKey,
            dtoClass: RawPayerMappingCsvRowDTO,
        })
    }

    /**
     * Convert raw CSV rows to request items.
     */
    static toRequestItems(
        items: RawPayerMappingCsvRowDTO[],
    ): IOVUpsertPayerMappingRequestData[] {
        return items.map(item => ({
            externalPayerId: item.externalPayerId,
            payerId: item.payerId,
            externalName: item.externalName,
        }))
    }

    /**
     * payer-service `row` is treated as a 0-based index into the items array we sent.
     */
    static mapBulkErrors(
        errors: IPayerServicePayerMappingsBulkRowError[],
    ): IIndexedValidationError[] {
        return errors.map(error => ({
            index: error.row,
            error: error.reason,
        }))
    }

    /**
     * Convert a payer mapping to a CSV row.
     */
    static mappingToCsvRow(item: IOneVerifyPayerMapping): string[] {
        const valueByKey: Record<PayerMappingsExportRowKey, string> = {
            externalName: item.externalName ?? "",
            externalPayerId: item.externalPayerId,
            payerId: item.payerId,
        }

        return Object.keys(PAYER_MAPPINGS_CONFIG.csvHeaderToKey).map(header => {
            const valueKey = PAYER_MAPPINGS_CONFIG.csvHeaderToKey[header]

            return valueKey ? (valueByKey[valueKey] ?? "") : ""
        })
    }
}
